import type { CSSProperties } from 'react'

import { maskPii, t } from '@ctcells/i18n'

import { cx } from '../../atoms/cx.ts'
import { ChamberView } from '../../figures/chamber3d/ChamberView.tsx'
import { useDisabled } from '../../atoms/disabled.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { ScreenState } from '../ScreenState.tsx'

import { BundleChamber, ChamberNoteLine } from './BundleChamber.tsx'
import {
  bundleCardModel,
  bundleColumns,
  bundleFitRows,
  bundleSequence,
  focusedWorking,
  orderBundles,
  sampleIdsText,
} from './bundleCardModel.ts'
import { PLACEHOLDER_CHAMBERS, placeholderBundles, type SampleBundle } from './patientChambers.ts'
import type { FlowCell } from './sampleFlowModel.ts'

import { useBoxHeightVar } from './useBoxMeasure.ts'

import './sampleFlow.css'

/**
 * 환자별 챔버 — **시료 탭의 «유일한» 그림.**
 *
 * eric 지시(2026-09-21 오후): 「환자별 그림 자체가 레이아웃이 깨져 있고 … 시료가 5개인데
 * **그림과 시료 아이디가 중복**돼. **그림 위주로** 해당 진행상황을 다시 그려줘.」
 * eric 지시(2026-09-22): 「와이어프레임 말고 **진짜 사용 서비스**가 나오도록 해줘.」(방안 A)
 *
 * ```
 * ┌ 환자 1  P1 ─────────────┐ ┌ 환자 2  P2 ─────────────┐
 * │ FE-0920-05              │ │ FE-0920-06              │
 * │   ▢ A1      ▢ A2        │ │   ▢ B1      ▢ B2        │  ← 담김 / 빈 / 실패
 * └─────────────────────────┘ └─────────────────────────┘
 *
 * 구역 머리에 배치 단계 줄이 한 벌 선다 — **이름 한 토큰씩만**이다(eric 2026-09-23).
 *
 * ```
 *  ● 등록   ● 원심   ◐ 어셈블   ○ 염색   ○ 완료
 * ```
 *
 * ## 🔴 한 카드가 같은 말을 세 번 하지 않는다 (2026-09-22)
 *
 * 종전에는 **챔버 안의 `●` · 단계 줄의 `●` · 상태 줄의 「등록 지남」**이 전부 같은 값
 * (`flowStateKey(bundle.state)`)에서 나왔다. 세 자리가 한 사실을 세 번 말하니 읽는 사람은
 * **세 가지 사실로** 읽는다. 이제 자리마다 축이 다르다 —
 *
 * | 자리 | 무엇을 말하나 | 출처 |
 * | --- | --- | --- |
 * | 챔버 그림 | 이 챔버에 시료가 들었나 (담김 · 빈 · 실패 · 끝남) | `chamberFill()` |
 * | 단계 줄(구역 머리) | 5단계 중 어디까지 | `FlowRow.cells` |
 *
 * ⚠ **시각 줄은 걷었다**(eric 2026-09-23 「이거 시간도 없애줘. 그냥 등록 원심 어셈블 염색 완료
 *   이거만 표시해. 시간도 위에 있잖아」) — 경과·시각은 **상시 작업 줄**이 이미 말한다.
 *   되살리면 같은 값이 한 화면에 두 번 선다.
 *
 * 🔴 **트레이 배치도가 아니다.** 칸의 «자리»를 그리면 우리가 모르는 것을 아는 척하게 된다
 *    (BR-31). 여기 있는 것은 **묶음의 구성**이다 — 웰 이름은 레시피 원문 그대로.
 * 🔴 **모든 카드가 같은 크기다.** ID 가 길면 **말줄임 + `title`** 이다. 폭을 늘리지 않는다.
 * 🔴 **한 시료의 챔버는 같은 상태다.** 챔버마다 다른 값을 지어내지 않는다.
 */
/**
 * **큰 카드로 설 수 있는 장수의 상한.** 넘으면 카드가 한 단계 작아진다
 * (`.bundles__list--dense` — 폭·칩·여백이 같이 준다).
 *
 * 🔴 **해상도가 아니라 «장수»로 가른다.** 고정 레이아웃이라 화면을 재서 바꾸지 않는다
 *    (CLAUDE.md). 경계 5 는 2026-09-26 에 「격자 3행(비전 + 카드 5장)」으로 잡은 값이다(종전 8.
 *    STL 모형 뒤 eric 「스크롤 발생 안 하게」).
 * ⚠ 같은 날 비전이 격자에서 빠졌다(→ 2026-09-28 오른쪽 고정 열 `VisionColumn` → 2026-10-01 회의로 시료별 보기에서 빠지고 그 자리는 소모품 판 `SupplyPanel`) — 이제 카드 5장은 3행(2·2·1)이고
 *    1행도 영상이 아니라 카드 키다. 경계는 **그대로 5** 로 두었다(스크롤 없음을 실측한 값만 쓴다).
 * ⚠ 크기를 정하는 것은 CSS 변수(`--chamber-row-h`·여백)이고 여기서는 «어느 쪽이냐»만 고른다.
 */
const ROOMY_MAX = 5

/**
 * **격자는 «두 열» 하나다**(eric 2026-09-23 「시료 화면에 2열 3행으로 …」).
 *
 * ```
 * ┌ 시료 1 ────────────────┐ ┌ 시료 2 ────────────────┐   ← 시료가 1번부터 앞에서 흐른다
 * ┌ 시료 3 ────────────────┐ ┌ 시료 4 ────────────────┐
 * ┌ 대조 ──────────────────┐                              ← 대조는 맨 뒤(eric 2026-09-28)
 * ```
 *
 * 🔴 **비전은 격자에 없다**(eric 2026-09-26 「비전 칸에서 빼고, 고정 상태바 그 아래 … 좌측 상단에
 *    위치시켜 줘」). 종전 1행 첫 칸이던 비전 카드는 떠 있는 창을 거쳐 2026-09-28 에 **판 오른쪽 고정 열**
 *    (`VisionColumn`)이 됐다가 2026-10-01 회의로 시료별 보기에서 빠졌다(2026-10-02 부터 카메라는 장비별 보기 상세 카드의 그 장비 칸 · 소모품은 오른쪽 열 판). 격자 칸으로 되살리지 마라.
 * 🔴 **자리를 CSS 로 «박지» 않는다 — DOM 차례가 곧 자리다**(시료 → 대조). 그래서
 *    **대조가 없는 배치에서 구멍이 안 생긴다**: 마지막 시료가 마지막 칸이다.
 * 🔴 **카드 수를 박는 것이 아니다** — 장수는 레시피가 정한다. 시료 4 이면 2행이 더 선다.
 * 🔴 **해상도가 아니라 장수다.** 화면 폭을 재서 열을 늘리지 않는다(고정 레이아웃 · CLAUDE.md).
 * ⚠ 종전의 «셋째 열 + 대조 세로 span»(`--bundle-rows`)은 걷었다. 되살리지 마라.
 */
export interface SampleBundlesProps {
  /** 🔴 **모든 시료가 여기 있다** — 묶음에 못 든 시료도 제 카드로 선다(`patientChambers()`) */
  bundles: readonly SampleBundle[]
  /*
   * ⚠ **작업 표기(`batchLabel`)는 걷었다**(eric 2026-09-23 「`fe-20260922-full-r1` 이 텍스트도
   *   빼줘」). 레시피 이름은 **상시 작업 줄**이 이미 말한다 — 같은 값을 두 자리에서 읽지 않는다.
   */
  /**
   * 🔴 **못 봤을 때의 사유.** `GET /samples` 는 viewer 에게 403 이다(PII 접촉면) —
   *    그때 빈 카드를 그리면 「시료가 없다」는 **거짓**이 된다. 넘어오면 그림 대신 이 문구가 선다.
   */
  notice?: string | undefined
  /**
   * **보고서를 못 본 사유**(`GET /reports/{sample_id}` 403 · 실패).
   * 🔴 **카드를 죽이지 않는다** — 구역 머리에 이 문구만 적는다. 보고서는 덧붙는 자료이고
   *    주 자료는 `GET /samples` 다(부가 조회 규율).
   * ⚠ 단계 줄의 시각은 걷었으므로(2026-09-23) 이 사유가 접는 것은 **카드의 「배치와 다름」
   *   줄에 붙는 마지막 표지 시각**과 실패 판정이다.
   */
  timesNotice?: string | undefined
  /**
   * 구독이 끊겨 **「지금」을 모른다.** 🔴 `true` 면 「진행 중」 칸이 한 칸도 없고
   *    (모델이 `runningDevice` 를 안 받는다) 그림 위에 그 사실을 적는다 —
   *    마지막으로 안 것을 **현재처럼 보이지 않는다**(`L1EventStream.stale`).
   */
  stale?: boolean | undefined
  /** `view` 권한. 없으면 시료 ID 를 마스킹한다(README §5-5) */
  canViewPii: boolean
  /*
   * ⚠ **「지금」(`now`)도 단계 셀의 시각도 걷었다**(2026-09-23). 카드의 「이후 N분」 문장에
   *   이어 단계 줄의 시각 줄까지 사라지면서 이 화면에 시계도 시각도 필요한 자리가 없다.
   */
  /** 카드를 누르면 그 시료 상세로. 안 넘기면 카드가 버튼이 아니라 글자다 */
  onOpenSample?: ((sampleId: string) => void) | undefined
  /**
   * **상세를 못 여는 사유**(viewer — 시료 상세 `GET /samples/{id}` 가 PII 접촉면이라 `view` 권한이 없으면 403).
   * 🔴 **숨기지 않는다** — 카드는 그대로 버튼이고 `›` 도 선다. 다만 `aria-disabled` + 사유(`title` · 보조기술 설명)이고
   *    눌러도 열리지 않는다(`useDisabled` 규약). 열어 봐야 사유 판뿐인 서랍을 띄우지 않는다.
   */
  openLocked?: string | undefined
  /** 오른쪽 판에 열린 시료(eric 2026-09-27). 그 카드만 「고른 카드」다 — 색과 `aria-current` 가 같이 말한다 */
  selectedSampleId?: string | undefined
  /**
   * **배치 단위 단계 5칸** — 🔴 **구역 머리에 한 벌만** 선다(eric 2026-09-23 「현재 진행 상태를
   * 한쪽으로 모는 건 어때?」). 종전에는 카드마다 같은 줄이 있었다.
   * 🔴 시료들의 다수결이 아니라 **배치 이벤트**가 출처다(`batchFlowCells` 머리말).
   * 안 넘기면 머리에 단계 줄이 없다 — 지어내지 않는다.
   */
  batchStages?: readonly FlowCell[] | undefined
  /*
   * ⚠ **셋째 열 판(`panel` · 데크 랙)은 걷었다**(eric 2026-09-28 「오른쪽은 제거해버려」). 소모품은 선택기 줄의
   *   요약 칩과 그 서랍이 말한다 — 시료 격자가 판 폭을 다 쓴다. 되살리지 마라.
   */
}

export function SampleBundles({
  bundles,
  notice,
  timesNotice,
  stale = false,
  canViewPii,
  onOpenSample,
  openLocked,
  selectedSampleId,
  batchStages,
}: SampleBundlesProps) {
  /*
   * 🔴 **도는 작업이 없어도 카드 자리는 선다**(eric 2026-09-22 「디폴트 4명 · 레퍼런스 하나를 계속 보이게」).
   *    카드는 이름 없는 점선 실루엣이다(`placeholderBundles`) — 마지막 작업으로 그 자리를 채우는 것이 아니고
   *    값은 전부 비어 있다. 「진행 중인 작업 없음」 문구도 적지 않는다(eric 「이 문구도 없애줘」) — 점선 자리
   *    카드 자체가 「아직 없음」을 말한다.
   */
  const idle = notice === undefined && bundles.length === 0
  const shown = idle ? placeholderBundles() : bundles
  /*
   * 🔴 **대조는 맨 뒤 — 격자의 마지막 칸(맨 아래)이다**(eric 2026-09-28 「시료 부분에서.. 대조를 맨 밑으로
   *    내리라고 했잖아」). 시료 카드가 1번부터 앞에서 흐르고 대조가 끝에 선다. 줄 정렬(`sortFlowRows`)과
   *    같은 한 함수(`referenceLast`)다. 점선 자리 카드(`placeholderBundles`)도 같은 차례다.
   *    CSS 로 `grid-column` · `grid-row` 를 박지 않는 까닭은 **대조가 없는 배치**다 — 자리를 박으면 그 칸이
   *    빈 구멍으로 남는다. 차례로 두면 저절로 찬다.
   * ⚠ 안정 분할이라 대조끼리·시료끼리의 차례는 서버가 준 그대로다.
   */
  /*
   * ⚠ 피펫이 든 웰의 「분주 중」(`withPipetteActive`)은 걷었다(eric 2026-10-14 「드리퍼랑 피펫 빼자」 → 추천안) — 피펫 자리를 짐작한 값이라
   *   실장비에서 어긋난다. 「분주 완료」(분주 기록 · 서버 사실)는 그대로다. 서버가 웰을 «진행»으로 주면 그때는 「분주 중」이 선다(`chamberNote`).
   */
  const ordered = orderBundles(shown)
  /*
   * 🔴 **카드 머리의 순번**(eric 2026-09-26 「모니터링 페이지에 시료 ID 빼라고. 순번으로 하라고」).
   *    대조를 빼고 **시료가 든 카드만** 1부터 센다. 모니터링 시료 서랍의 머리도 **같은 함수**로 센다.
   * ⚠ 서버 값이 아니라 **화면 안의 차례**다 — 다른 화면(시료 목록의 서랍 등)에 이 번호를 들고 가지 마라.
   */
  const seqOf = bundleSequence(ordered)
  /* 시료 탭 판 키(`--bundles-plate-h`) — 그림 폭 한도 · 행 최소 키가 읽는다(`sampleFlow.css`) */
  const plateRef = useBoxHeightVar<HTMLElement>('--bundles-plate-h')
  /* 🔴 작업 중 강조는 화면 전체를 보고 고른다 — 전부 작업 중이면 걷는다(`focusedWorking` 머리말 · eric 2026-10-14) */
  const workingOf = focusedWorking({
    bundles: ordered,
    batchStages: batchStages ?? [],
    stale,
    pipetteWell: null,
  })
  return (
    <section aria-labelledby="bundlehead" className="bundles" ref={plateRef}>
      {/*
        🔴 **제목 글자와 레시피 이름은 «화면에서» 걷었다**(eric 2026-09-23 「`시료별 챔버`
           `fe-20260922-full-r1` 이 텍스트도 빼줘」). 레시피 이름은 상시 작업 줄에 이미 있다.
        🔴 **구역 이름은 «지우지» 않는다** — 보조기술은 이 구역이 무엇인지 알아야 한다.
           그래서 제목은 `.a-sr` 로 남고 `aria-labelledby` 가 그대로 가리킨다.
      */}
      <h3 className="a-sr" id="bundlehead">
        {t('monitoring.flow.groups.title')}
      </h3>

      {/*
        ⚠ **구역 머리의 배치 단계 줄(`.stagerail`)은 «걷었다»** — eric 2026-09-23 「우측 상단에
          이건 빼줘」(그 줄의 글자 `● 등록 지남 ● 원심 지남 … ○ 완료 대기` 를 붙여 넣으면서).
        🔴 **되살리지 마라.** 그 열 낱말이 eric 이 말한 「모든 이벤트」다 — 같은 날 「각각의 챔버
           아래에 모든 이벤트가 나오지 말고 **어디까지 끝냈는지만** 간단하게」와 **한 쌍의 지시**다.
           단계는 이제 **챔버 아래 한 마디**(`.chamber__note`)와 상시 작업 줄이 맡는다.
        ⚠ `batchStages` 는 그대로 받는다 — 챔버의 «공정 모양»과 그 한 마디가 그 값에서 나온다
          (`chamberPhase`). 프롭이 남아 있다고 줄을 되살리지 마라.
      */}
      {/*
        🔴 **끊김을 그림 «위»에 적는다.** 카드를 지우지 않는 까닭은 「지남」이 여전히 서버가 한
           말이기 때문이다 — 모르게 된 것은 **「지금」** 하나이고, 그 한 가지를 정확히 말한다.
      */}
      {stale ? (
        <p className="bundles__stale" role="status">
          {t('monitoring.flow.stale')}
        </p>
      ) : null}

      {/*
        🔴 **시각을 못 본 것은 «시각 줄»만의 사실이다.** 카드를 접지 않는다 —
           보고서 경로가 403 이어도 시료·단계는 `GET /samples` 가 말해 준 그대로다.
      */}
      {timesNotice === undefined || notice !== undefined ? null : (
        /* 한 줄 · 말줄임이다(행 키 셈이 그 몫을 뺀다 · `sampleFlow.css`) — 잘린 글자는 툴팁이 말한다 */
        <p className="bundles__timesnote" role="status" title={timesNotice}>
          {timesNotice}
        </p>
      )}

      {notice !== undefined ? (
        <ScreenState text={notice} />
      ) : (
        /*
          카드 상자 — 카드가 늘면 이 안에서 세로로 구른다(`.bundles__wrap`).
          ⚠ 3D 캔버스 감싸개(`Chamber3DHost`)는 2026-10-01 회의 「3D → 2D」로 걷었다. 챔버는 늘 2D 다.
        */
        <div className="bundles__wrap">
          {/*
            🔴 **두 열 · 차례대로**(eric 2026-09-23). 시료가 앞에서 흐르고 대조가 맨 뒤다(eric 2026-09-28)
               (비전은 2026-09-26 에 격자에서 빠졌다 — 2026-10-01 부터 판 오른쪽은 소모품 판 `SupplyPanel`).
               자리를 지정하는 코드가 한 줄도 없다 — **차례가 곧 자리**라 대조가 없어도 구멍이 없다.
          */}
          <ul
            className={cx(
              'bundles__list',
              shown.length > ROOMY_MAX && 'bundles__list--dense',
              /* 6장을 넘으면 세 열(`bundleColumns` — eric 09-23 「2열 3행」은 6장 이하에서 그대로) */
              bundleColumns(shown.length) === 3 && 'bundles__list--three',
            )}
            /* 🔴 줄 수가 장수를 따라간다 — 판 키를 그만큼 나눠 다 든다(`bundleFitRows`) */
            style={{ '--bundle-fit-rows': String(bundleFitRows(shown.length)) } as CSSProperties}
          >
            {ordered.map((bundle) => (
              <BundleCard
                batchStages={batchStages ?? []}
                bundle={bundle}
                canViewPii={canViewPii}
                key={bundle.key}
                onOpen={onOpenSample}
                openLocked={openLocked}
                selected={selectedSampleId !== undefined && bundle.sampleId === selectedSampleId}
                seq={seqOf.get(bundle.key)}
                working={workingOf.get(bundle.key) ?? NO_WELLS}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

const NO_WELLS: ReadonlySet<string> = new Set()

function BundleCard({
  bundle,
  canViewPii,
  onOpen,
  openLocked,
  batchStages,
  seq,
  selected,
  working,
}: {
  bundle: SampleBundle
  /** 강조할 작업 중 챔버(웰 이름) — 화면 전체를 보고 `focusedWorking` 이 고른다. 끊기면 비어 있다 */
  working: ReadonlySet<string>
  canViewPii: boolean
  onOpen: ((sampleId: string) => void) | undefined
  /** 상세를 못 여는 사유(viewer). 있으면 카드는 `aria-disabled` 버튼이다 */
  openLocked: string | undefined
  /** 오른쪽 판에 열린 시료의 카드인가 */
  selected: boolean
  /** 카드 머리의 순번(대조·배정 없음 카드는 `undefined`) */
  seq: number | undefined
  /** 배치 단계 5칸 — 챔버의 공정 자리를 여기서 읽는다 */
  batchStages: readonly FlowCell[]
}) {
  /*
   * 🔴 **카드 머리 = 왼쪽 위 제목(「시료 1」 · 대조는 「대조」) + 오른쪽 위 `›` / 아래 줄 시료 번호**
   *    (디자인팀 시안 2026-10-01 §3 「시료 카드 제목」 · 「카드 상세 이동」). ID 줄은 `SampleId` — 자르지 않는다.
   *    viewer 에게는 ID 줄이 없다.
   * ⚠ 종전(eric 2026-09-28)에는 순번 · 대조가 오른쪽 위 칩이었다. 시안이 제목으로 옮겼다 — 대조의 «검은 배지» 뜻은
   *   제목 글자(「대조」)와 카드 바탕 톤(`.bundle--ref`)이 이어 말한다(시안 §5 「수정본에서는 카드 제목이 "대조"」).
   * 🔴 **챔버에 입히는 것(공정 모양 · 아래 한 마디 · 빈 까닭)은 `bundleCardModel` 한 벌이다** —
   *    모니터링 시료 서랍이 같은 함수를 부른다. 여기서 따로 계산하면 카드와 서랍이 다른 말을 한다.
   */
  const { phase, note, vacancy } = bundleCardModel(bundle, batchStages)
  /*
   * 🔴 **작업 중인 챔버**(2026-10-01 회의 「9개 중 현재 작업 중인 칩이 구분되어야 함. 스텝 상세는 불필요」) — 챔버마다
   *    `chamberWorking()` 한 함수로 가른다(챔버 한 마디가 「진행」일 때와 같은 값 · 끊기면 아무것도 아니다).
   *    하나라도 작업 중이면 카드 머리에 「작업 중」 배지와 카드 테가 선다 — 아홉 칩 중 어디가 도는지 카드 단위로도 보인다.
   */
  const sampleId = bundle.sampleId
  const linked = onOpen !== undefined && sampleId !== undefined
  const { isDisabled, ariaProps, reasonNode } = useDisabled(
    linked && openLocked !== undefined,
    openLocked,
  )

  const body = (
    <>
      <span className="bundle__head">
        <SampleName bundle={bundle} canViewPii={canViewPii} seq={seq} working={working.size > 0} />
        {linked ? <MoreMark /> : null}
        <SampleId bundle={bundle} canViewPii={canViewPii} />
      </span>

      <span className="bundle__chambers">
        {bundle.placeholder === true ? (
          /*
           * 자리만 서는 카드(작업이 진행 중이 아님) — 이름 없는 점선 챔버 실루엣만(eric 2026-10-08 「그 때도 챔버 보이게 해야지」).
           * 실루엣은 읽을 값이 없어 스크린리더에는 숨긴다 — 아래 카드 한 줄이 그 사실을 읽힌다
           */
          Array.from({ length: PLACEHOLDER_CHAMBERS[bundle.role] }, (_, i) => (
            <span aria-hidden="true" className="chamber chamber--unassigned" key={i}>
              <ChamberView state="unassigned" well="" />
            </span>
          ))
        ) : bundle.chambers.length === 0 ? (
          /*
           * 🔴 **챔버만 모르는 것이지 단계는 안다.** 챔버가 없으면 한 마디를 걸 자리도 없어지므로
           *    여기에 같이 세운다 — 종전에 카드 아래 편차 한 줄이 하던 몫이다(그 줄은 걷었다).
           */
          <span className="bundle__nochamber">
            {t('monitoring.flow.groups.noChamber')}
            {note === undefined ? null : <ChamberNoteLine note={note} />}
          </span>
        ) : (
          bundle.chambers.map((dot) => (
            <BundleChamber
              dot={dot}
              key={dot.well}
              working={working.has(dot.well)}
              {...(vacancy === undefined ? {} : { vacancy })}
              {...(note === undefined ? {} : { note })}
              {...(phase === undefined ? {} : { phase })}
            />
          ))
        )}
      </span>

      {bundle.placeholder === true ? (
        /*
         * 🔴 「챔버 배정 정보 없음」은 **카드에 한 번** — 카드 바닥 한 줄 · 보조 글자(eric 2026-10-13 「글자 크기가 너무 크고.. 두께가 너무
         *    두꺼워」). 종전엔 챔버마다 아래에 칩 글자 단(콘솔 18 · 반굵게)으로 같은 말이 두세 번 서서 옆 칸까지 넘쳤다
         *    (eric 2026-10-08 「챔버 배정 정보 없음이라고 해줘.. 작업이 진행 중이지 않으면」)
         */
        <span className="bundle__noassign" data-part="no-assignment">
          {t('monitoring.flow.groups.noAssignment')}
        </span>
      ) : null}

      {/*
        ⚠ **카드 아래 편차 한 줄(`.bundle__diff`)도 걷었다**(2026-09-23). 그 줄이 말하던 것
          (「원심 폐기」·「원심 실패」·「원심 대기」)은 이제 **챔버 아래 한 마디가 그대로** 말한다 —
          한 카드가 같은 사실을 두 번 적지 않는다. 🔴 되살리면 챔버는 「원심 완료」, 카드는
          「원심 폐기」라고 **한 화면이 두 말을** 한다.
      */}
    </>
  )

  return (
    <li
      className={cx(
        'bundle',
        bundle.role === 'reference' && 'bundle--ref',
        bundle.status === 'discarded' && 'bundle--halted',
        bundle.placeholder === true && 'bundle--placeholder',
        selected && 'bundle--selected',
        working.size > 0 && 'bundle--working',
      )}
    >
      {onOpen !== undefined && sampleId !== undefined ? (
        /*
         * 🔴 **못 열어도 숨기지 않는다**(viewer) — 같은 버튼이 `aria-disabled` + 사유로 선다. 포커스는 살아
         *    보조기술이 사유를 읽는다(`useDisabled`). 누름은 여기서 막는다.
         */
        <button
          {...(selected ? { 'aria-current': 'true' as const } : {})}
          {...ariaProps}
          className={cx('bundle__card', 'bundle__card--link', isDisabled && 'bundle__card--locked')}
          onClick={() => {
            if (!isDisabled) onOpen(sampleId)
          }}
          type="button"
        >
          {body}
          {reasonNode}
        </button>
      ) : (
        <span className="bundle__card">{body}</span>
      )}
    </li>
  )
}

/**
 * 시료 번호 한 줄 — **카드 머리 아래, 전체를 그대로**(디자인팀 시안 2026-10-01 · `DESIGN-CHANGES.md` §3 「시료 카드 제목」:
 * 「실물 튜브 라벨과 대조할 수 있어야 뒤바뀜을 막을 수 있음」).
 *
 * ⚠ 2026-09-26 에 eric 이 「모니터링 페이지에 시료 ID 빼라고. 순번으로 하라고」로 ID 를 머리에서 내렸고(`SampleName` 주석),
 *   2026-10-01 디자인팀 시안이 **순번은 두고 ID 를 «한 줄 더»로** 되올렸다 — 순번(대조는 「대조」)이 왼쪽 위 제목이고 ID 가 그 아래 선다.
 * 🔴 **자르지 않는다** — 고정폭 · 한 줄(`nowrap`) · 말줄임 없음. 번호 끝자리가 잘리면 대조하는 뜻이 없다(`sampleFlow.css`).
 * 🔴 **viewer 에게는 없다.** 가린 값(`••••`)을 한 줄 더 그리는 것은 소음이라 `canViewPii` 가 아니면 줄 자체가 안 선다 —
 *    보조기술 문장(`SampleName` 의 `.a-sr` 「시료 번호 ••••」)이 종전대로 말한다.
 * 🔴 보조기술에는 숨긴다(`aria-hidden`) — 같은 ID 를 `SampleName` 의 `.a-sr` 이 이미 순번과 함께 읽는다. 두 번 읽지 않는다.
 * ⚠ 시료 없는 묶음 · 자리 카드는 줄이 없다(머리는 비어 있다 — eric 2026-09-26).
 */
function SampleId({ bundle, canViewPii }: { bundle: SampleBundle; canViewPii: boolean }) {
  if (bundle.sampleId === undefined || !canViewPii) return null
  return (
    <span aria-hidden="true" className="bundle__id mono">
      {sampleIdsText(bundle.sampleIds ?? [bundle.sampleId])}
    </span>
  )
}

/**
 * **상세 보기 표지 `›`** — 카드 오른쪽 위(디자인팀 시안 2026-10-01 §3 「카드 상세 이동」 ·
 * 「카드를 눌러 상세로 갈 수 있다는 것을 조용히 알림」).
 *
 * 🔴 **버튼이 아니다** — 카드 전체가 이미 버튼이고(`bundle__card--link`) 이건 그 안의 조용한 표지다. 버튼 안에
 *    버튼을 두지 않는다. 평소에는 `›` 만 보이고, 마우스를 올리거나 키보드 초점이 오면 「상세 보기」 글자가 선다.
 * 🔴 글자 「상세 보기」는 **늘 DOM 에 있다** — 숨길 때도 클립(`.a-sr` 꼴)이라 카드 버튼의 접근 이름 끝에 늘 붙는다.
 *    `display: none` 으로 숨기면 보조기술에서도 사라진다.
 * ⚠ 글리프는 글꼴 글자가 아니라 작은 선 그림이다 — `Icon` 원자에 갈매기 꼴이 없어 여기 한 벌만 둔다(시안 `chev()`).
 */
function MoreMark() {
  return (
    <span className="bundle__more">
      <span className="bundle__moretext">{t('monitoring.flow.groups.openDetail')}</span>
      <svg aria-hidden="true" className="bundle__chev" focusable="false" viewBox="0 0 16 16">
        <path d="M6 3.5 10.5 8 6 12.5" />
      </svg>
    </span>
  )
}

/**
 * 카드 제목 — **왼쪽 위 「시료 1」**(대조는 「대조」)이고 시료 ID 는 `title` 과 보조기술, 그리고 아래 한 줄(`SampleId`)에 있다.
 *
 * eric 지시(2026-09-26): 「모니터링 페이지에 시료 ID 빼라고. 순번으로 하라고.」 · 「너무 복잡한데」.
 * ⚠ 2026-10-01 디자인팀 시안 §3 — 순번이 오른쪽 위 칩에서 **왼쪽 위 제목**(구역 제목 단 · 굵게)으로 옮겼고
 *   ID 가 **머리 아래 한 줄**(`SampleId`)로 섰다. 대조 카드는 제목이 「대조」다(시안 §5 — 종전 검은 배지의 뜻).
 * 🔴 **마스킹된 글자가 곧 `title` 이다**(종전 규칙 그대로). 말줄임을 풀어 보이는 곳에 원값을 흘리면
 *    마스킹이 무의미해진다 — viewer 는 풍선에서도 `••••` 다. 보조기술에는 「시료 번호」 라벨이 붙는다.
 * ⚠ 여기 가려지는 것은 **시료 ID** 다. 환자 번호(`patient_id`)는 이 카드에 애초에 안 실린다.
 */
function SampleName({
  bundle,
  canViewPii,
  seq,
  working,
}: {
  bundle: SampleBundle
  canViewPii: boolean
  seq: number | undefined
  /** 이 카드의 챔버 중 하나라도 작업 중 — 제목 옆에 「작업 중」 배지(글자가 말한다 · 색은 얹을 뿐) */
  working: boolean
}) {
  if (bundle.sampleId === undefined) {
    /*
     * 🔴 **머리는 비운다**(eric 2026-09-26 「‘배정 정보 없음’ 이 문구도 빼줘 그냥. 그 밑에 표현하면 됨」).
     *    시료가 없는 까닭은 **챔버 아래 한 마디**(`VacancyNote`)가 말한다 — 머리에 되올리지 마라.
     *    자리(`.bundle__sample`)는 남겨 순번 카드와 머리 높이를 맞춘다(격자 줄 맞춤).
     */
    return <span className="bundle__sample" />
  }
  const label = maskPii(bundle.sampleId, canViewPii)
  const spoken = canViewPii ? label : `${t('monitoring.flow.maskedSample')} ${label}`
  /* 제목 글자 — 대조는 「대조」, 시료는 순번(「시료 1」). 둘 다 아니면(묶음에 못 든 시료) 제목 없이 보조기술 문장만 */
  const title =
    bundle.role === 'reference'
      ? t('monitoring.reference')
      : seq === undefined
        ? undefined
        : t('monitoring.flow.groups.patient', { index: String(seq) })
  return (
    <span className="bundle__sample" title={label}>
      {title === undefined ? null : (
        /* 보이는 제목은 보조기술에 숨긴다 — 아래 `.a-sr` 문장이 제목과 번호를 한 번에 읽는다 */
        <span
          aria-hidden="true"
          className={cx(
            'bundle__title',
            bundle.role === 'reference' ? 'bundle__title--ref' : 'bundle__seq',
          )}
        >
          {title}
        </span>
      )}
      <span className="a-sr">{title === undefined ? spoken : `${title} · ${spoken}`}</span>
      {working ? (
        /* 「작업 중」은 진행 칩과 같은 초록(eric 2026-10-12 「실행 중은.. 초록색으로」 · `sampleFlow.css`) */
        <span className="bundle__working">
          <StatusBadge label={t('monitoring.flow.groups.working')} status="running" />
        </span>
      ) : null}
    </span>
  )
}

/*
 * ⚠ **`StageRail`·`RailStep` 은 «걷었다»**(2026-09-23 · eric 「우측 상단에 이건 빼줘」).
 * 🔴 **되살리지 마라.** 단계는 **챔버 아래 한 마디**가 말하고, 경과·시각은 **상시 작업 줄**이 말한다.
 * ⚠ 챔버 한 개(`BundleChamber`)와 그 모델(`bundleCardModel`)은 2026-09-28 에 파일을 나눴다 —
 *   모니터링 시료 서랍이 같은 것을 쓴다.
 */
